import { WebpageTreeNode } from './types';

export interface FlatRow {
  node: WebpageTreeNode;
  level: number;
  /** Is this node the last among its siblings? Drives └── vs ├──. */
  isLast: boolean;
  /**
   * One flag per intermediate ancestor (depths 1..level-1, i.e. excluding the
   * top-level root): does that ancestor have a following sibling? If so, a
   * vertical guide line (│) runs down through this row at that indent, exactly
   * like the `tree` command. Length is `level - 1` (empty for levels 0 and 1).
   */
  pathFlags: boolean[];
}

/**
 * Depth-first flatten to the currently visible rows: a node's children are only
 * included when the node's path is in `expanded`. Also computes the `tree`-style
 * connector metadata (`isLast`, `pathFlags`) for each visible row.
 */
export function flattenTree(
  nodes: WebpageTreeNode[],
  expanded: Set<string>,
  level = 0,
  pathFlags: boolean[] = []
): FlatRow[] {
  const rows: FlatRow[] = [];
  nodes.forEach((node, index) => {
    const isLast = index === nodes.length - 1;
    rows.push({ node, level, isLast, pathFlags });
    if (node.children && expanded.has(node.path)) {
      // Top-level roots draw no vertical guide for their children (siblings sit
      // directly below at the same indent); deeper ancestors add a │ that
      // continues only while they have a following sibling.
      const childFlags = level === 0 ? [] : [...pathFlags, !isLast];
      rows.push(...flattenTree(node.children, expanded, level + 1, childFlags));
    }
  });
  return rows;
}
